import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import { useAuth } from '../../utils/authContext';
import { login } from '../../utils/api';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

// Color scheme to match homePage.js
const colors = {
  primary: '#3a86ff',
  secondary: '#ff006e',
  accent: '#8338ec',
  success: '#06d6a0',
  warning: '#ffbe0b',
  dark: '#1e2a3a',
  light: '#f8f9fa',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
};

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const { login: authLogin, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  // Si l'utilisateur est déjà connecté, rediriger vers le dashboard
  useEffect(() => {
    console.log("Login: Composant chargé, état d'authentification:", { isLoggedIn });
    if (isLoggedIn) {
      console.log('Login: Utilisateur déjà connecté, redirection vers dashboard');
      navigate('/admin/dashboard');
    }
  }, [isLoggedIn, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Login: Tentative de connexion avec email:', email);
    setError('');
    setLoading(true);

    try {
      const data = await login(email, password);
      console.log('Login: Connexion réussie, données reçues:', {
        token: data.token ? '***' : 'absent',
        userId: data.userId,
        userRole: data.userRole,
        userEmail: data.userEmail
      });

      if (!data.token) {
        throw new Error("Le token d'authentification est manquant dans la réponse");
      }

      // Stocker toutes les données d'authentification
      localStorage.setItem('userId', data.userId);
      localStorage.setItem('userRole', data.userRole);
      localStorage.setItem('userEmail', data.userEmail);

      // Stocker le token dans localStorage via le context d'authentification
      authLogin(data.token);
      console.log('Login: Toutes les données stockées:', {
        token: '***',
        userId: data.userId,
        userRole: data.userRole,
        userEmail: data.userEmail
      });

      // Vérifier que le stockage a bien fonctionné
      const storedToken = localStorage.getItem('token');
      console.log('Login: Vérification du stockage - token présent:', !!storedToken);

      // Utiliser navigate au lieu de window.location pour éviter un rechargement complet
      console.log('Login: Redirection vers le dashboard');
      navigate('/admin/dashboard');
    }
    catch (err) {
      console.error('Login: Erreur lors de la connexion', err);
      setError(err.message || 'Une erreur est survenue lors de la connexion');
    }
    finally {
      setLoading(false);
    }
  };

  const handleFocus = (field) => {
    setFocusedField(field);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      transition: 'all 0.3s ease',
      backgroundColor: '#f1f5f9',
      '&.Mui-focused': {
        boxShadow: `0 0 0 2px ${colors.primary}40`,
      },
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover fieldset': {
        borderColor: 'transparent',
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.primary,
        borderLeftWidth: '4px',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      '&.Mui-focused': {
        color: colors.primary,
      },
    },
    mb: 3,
  };

  const buttonStyle = {
    borderRadius: '12px',
    padding: '16px 36px',
    fontWeight: '600',
    textTransform: 'none',
    fontSize: '1.125rem',
    boxShadow: '0 10px 25px rgba(58, 134, 255, 0.3)',
    transition: 'all 0.3s ease',
    backgroundColor: colors.primary,
    '&:hover': {
      backgroundColor: colors.primary,
      boxShadow: '0 15px 30px rgba(58, 134, 255, 0.4)',
      transform: 'translateY(-3px)',
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          position: 'relative',
        }}
      >
        {/* Background decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            right: '5%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(58, 134, 255, 0.1) 0%, transparent 70%)',
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            left: '5%',
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 0, 110, 0.1) 0%, transparent 70%)',
            zIndex: 0,
          }}
        />

        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          style={{ width: '100%', position: 'relative', zIndex: 1 }}
        >
          <Paper
            elevation={0}
            sx={{
              padding: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              borderRadius: '24px',
              boxShadow: colors.boxShadow,
              position: 'relative',
              overflow: 'hidden',
              border: '1px solid rgba(0, 0, 0, 0.05)',
            }}
          >
            {/* Decorative top bar */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '5px',
                background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
              }}
            />

            <Typography
              component="h1"
              variant="h4"
              sx={{
                mb: 4,
                fontWeight: 800,
                color: colors.dark,
                background: 'linear-gradient(90deg, #1e2a3a 0%, #3a4a5a 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                position: 'relative',
                display: 'inline-block',
              }}
            >
              Connexion Admin
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '40px',
                  height: '4px',
                  background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                  borderRadius: '2px',
                }}
              />
            </Typography>

            {error && (
              <Alert
                severity="error"
                sx={{
                  width: '100%',
                  mb: 3,
                  borderRadius: '12px',
                  '& .MuiAlert-icon': {
                    color: colors.secondary,
                  }
                }}
              >
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  color: colors.dark
                }}
              >
                Adresse e-mail
              </Typography>
              <TextField
                required
                fullWidth
                id="email"
                name="email"
                autoComplete="email"
                placeholder="Votre adresse e-mail"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => handleFocus('email')}
                onBlur={handleBlur}
                sx={{
                  ...inputStyle,
                  '& .MuiOutlinedInput-root': {
                    ...inputStyle['& .MuiOutlinedInput-root'],
                    ...(focusedField === 'email' && {
                      '& fieldset': {
                        borderColor: colors.primary,
                        borderLeftWidth: '4px',
                      }
                    })
                  }
                }}
              />

              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  color: colors.dark
                }}
              >
                Mot de passe
              </Typography>
              <TextField
                required
                fullWidth
                name="password"
                type="password"
                id="password"
                autoComplete="current-password"
                placeholder="Votre mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => handleFocus('password')}
                onBlur={handleBlur}
                sx={{
                  ...inputStyle,
                  '& .MuiOutlinedInput-root': {
                    ...inputStyle['& .MuiOutlinedInput-root'],
                    ...(focusedField === 'password' && {
                      '& fieldset': {
                        borderColor: colors.primary,
                        borderLeftWidth: '4px',
                      }
                    })
                  }
                }}
              />

              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ ...buttonStyle, mt: 3 }}
                  disabled={loading}
                >
                  {loading ? 'Connexion en cours...' : 'Se connecter'}
                </Button>
              </motion.div>
            </Box>

            {/* Decorative elements */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '150px',
                height: '150px',
                background: 'radial-gradient(circle, rgba(58, 134, 255, 0.05) 0%, rgba(255, 255, 255, 0) 70%)',
                zIndex: 0
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '120px',
                height: '120px',
                background: 'radial-gradient(circle, rgba(255, 0, 110, 0.05) 0%, rgba(255, 255, 255, 0) 70%)',
                zIndex: 0
              }}
            />
          </Paper>
        </motion.div>
      </Box>
    </Container>
  );
}

export default Login;
